import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import { IconButton, Stack } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import Konva from 'konva';
import { Stage } from 'konva/lib/Stage';
import { cloneDeep } from 'lodash';
import * as React from 'react';
import { HtmlImageContext } from '../../App';
import { roundTwoDecimal } from '../../calculation/util';
import { EDrainageTrenchType, EEngineType, EUnitOfMeasure } from '../../shared/enum';
import { useWindowSize } from '../../shared/hook/hook';
import {
    setGeneralViewImage,
    setLongitudinalSeismicSectionImage,
    setLongitudinalStaticSectionImage,
    setTransversalSectionPrimaryDrainageTrenchImage,
    setTransversalSectionSecondaryDrainageTrenchImage,
} from '../../store/Image/imageSlice';
import { EReduxState } from '../../store/enum';
import { useAppDispatch, useAppSelector } from '../../store/hook';
import { selectProject } from '../../store/project/projectSlice';
import { selectResult } from '../../store/result/resultSlice';
import { theme } from '../../style';
import { ILegend, Legend } from './Utils';
import generalView from './generalView';
import drainHorizontalLegend from './image/drainHorizontalLegend.png';
import drainPipeLegend from './image/drainPipeLegend.png';
import drainageLayingPlanLegend from './image/drainageLayingPlanLegend.png';
import fsSeismicMinLegend from './image/fsSeismicMinLegend.png';
import fsStaticMinLegend from './image/fsStaticMinLegend.png';
import grid from './image/grid_pattern.svg';
import initialWaterLegend from './image/initialWaterLegend.png';
import overloadLegend from './image/overloadLegend.png';
import postFsSeismicLegend from './image/postFsSeismicLegend.png';
import postFsStaticLegend from './image/postFsStaticLegend.png';
import preFsSeismicLegend from './image/preFsSeismicLegend.png';
import preFsStaticLegend from './image/preFsStaticLegend.png';
import longitudinalSection from './longitudinalSection';
import transversalSection from './transversalSection';

interface IEngineProps {
    type: EEngineType;
    withExport?: boolean;
    onlySlopeAndLoad?: boolean;
}

export interface IResultCalculationSrcImage {
    axis?: string | HTMLImageElement;
    preFsStatic?: string | HTMLImageElement;
    postFsStatic?: string | HTMLImageElement;
    preFsSeismic?: string | HTMLImageElement;
    postFsSeismic?: string | HTMLImageElement;
}

export interface ISrcImage {
    generalViewDoubleWithSecondaryDrain?: string | HTMLImageElement;
    generalViewDoubleWithoutSecondaryDrain?: string | HTMLImageElement;
    generalViewSingleWithSecondaryDrain?: string | HTMLImageElement;
    generalViewSingleWithoutSecondaryDrain?: string | HTMLImageElement;
    impermeableLayerPattern?: string | HTMLImageElement;
    waterPattern?: string | HTMLImageElement;
    drainPattern?: string | HTMLImageElement;
    groundwaterPattern?: string | HTMLImageElement;
    resultCalculation?: IResultCalculationSrcImage;
}

var stage: Stage;

const drain = 'drain';
const drainageLayingPlan = 'drainageLayingPlan';
const drainPipe = 'drainPipe';
const initialGroudwater = 'initialGroudwater';
const axis = 'axis';
const preFsStatic = 'preFsStatic';
const postFsStatic = 'postFsStatic';
const preFsSeismic = 'preFsSeismic';
const postFsSeismic = 'postFsSeismic';

const distributedOverloadLegend = 'distributedOverloadLegend';

type TFsPreAndPost = typeof preFsStatic | typeof postFsStatic | typeof preFsSeismic | typeof postFsSeismic;

export default (props: IEngineProps) => {
    const canvasRef = React.useRef<HTMLDivElement>(null);

    const [legendData, setLegendData] = React.useState<ILegend[]>();

    const htmlImgObjContext = React.useContext(HtmlImageContext);
    let htmlImgObj = cloneDeep(htmlImgObjContext);

    const sizeWindow = useWindowSize();
    const matches = useMediaQuery(`(min-width:${theme.breakpoints.values.md}px)`);

    const project = useAppSelector(selectProject);
    const result = useAppSelector(selectResult);
    const dispatch = useAppDispatch();

    const mToPixel = 20;
    const fttToPixel = 6;

    const convertToPixel = project.data?.projectInfo?.unitOfMeasure == EUnitOfMeasure.Metric ? mToPixel : fttToPixel;

    let [reset, setReset] = React.useState<boolean>();

    const handleZoom = (direction: number, scaleBy: number) => {
        var oldScale = stage.scaleX();
        var mousePointTo = {
            x: (stage.width() / 2 - stage.x()) / oldScale,
            y: (stage.height() / 2 - stage.y()) / oldScale,
        };
        var newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;
        stage.scale({ x: newScale, y: newScale });
        var newPos = {
            x: stage.width() / 2 - mousePointTo.x * newScale,
            y: stage.height() / 2 - mousePointTo.y * newScale,
        };
        stage.position(newPos);
    };

    const handleChangeLineLongitudinalSection = (event: React.ChangeEvent<any>) => {
        let { name, checked } = event.target;
        if (checked) htmlImgObj!.resultCalculation![name as TFsPreAndPost] = htmlImgObjContext?.resultCalculation?.[name as TFsPreAndPost];
        else htmlImgObj!.resultCalculation![name as TFsPreAndPost] = undefined;

        if (htmlImgObj?.resultCalculation?.postFsSeismic || htmlImgObj?.resultCalculation?.postFsStatic || htmlImgObj?.resultCalculation?.preFsSeismic || htmlImgObj?.resultCalculation?.preFsStatic) {
            htmlImgObj!.resultCalculation!.axis = htmlImgObjContext?.resultCalculation?.axis;
        }

        draw();
    };

    const draw = () => {
        let engine;
        let legendArr: ILegend[];
        stage = new Konva.Stage({
            container: canvasRef.current!,
            width: canvasRef.current?.clientWidth!,
            height: canvasRef.current?.clientHeight!,
        });

        switch (props.type) {
            case EEngineType.PreliminaryCalculation:
                break;
            case EEngineType.GeneralView:
                engine = generalView(stage, htmlImgObj!);
                break;
            case EEngineType.LongitudinalSection:
                legendArr = [{ name: initialGroudwater, logo: initialWaterLegend }];
                if (!props.onlySlopeAndLoad) {
                    legendArr.push({ name: drainageLayingPlan, logo: drainageLayingPlanLegend }, { name: drain, logo: drainHorizontalLegend });
                    if (htmlImgObj?.resultCalculation) {
                        if (result.status == EReduxState.fulfilled) {
                            legendArr.push(
                                { name: 'minFsStaticConditionsWithValue', logo: fsStaticMinLegend, value: roundTwoDecimal(project.data?.load?.minFsStaticConditions!) },
                                { name: preFsStatic, logo: preFsStaticLegend, onChange: handleChangeLineLongitudinalSection },
                                { name: postFsStatic, logo: postFsStaticLegend, onChange: handleChangeLineLongitudinalSection },
                            );
                            if (project.data?.load?.hasSeismicLoads) {
                                legendArr.push(
                                    { name: 'minFsSeismicConditionsWithValue', logo: fsSeismicMinLegend, value: roundTwoDecimal(project.data?.load?.minFsSeismicConditions!) },
                                    { name: preFsSeismic, logo: preFsSeismicLegend, onChange: handleChangeLineLongitudinalSection },
                                    { name: postFsSeismic, logo: postFsSeismicLegend, onChange: handleChangeLineLongitudinalSection },
                                );
                            }
                        } else {
                            htmlImgObj!.resultCalculation[axis] = undefined;
                            htmlImgObj!.resultCalculation[preFsSeismic] = undefined;
                            htmlImgObj!.resultCalculation[postFsSeismic] = undefined;
                            htmlImgObj!.resultCalculation[preFsStatic] = undefined;
                            htmlImgObj!.resultCalculation[postFsStatic] = undefined;
                        }
                    }
                }
                if (project.data?.load?.distributedOverload! > 0) {
                    legendArr.push({ name: distributedOverloadLegend, logo: overloadLegend, width: 10, height: 15 });
                }
                setLegendData(legendArr);
                engine = longitudinalSection(stage, htmlImgObj!, convertToPixel!, props.onlySlopeAndLoad, props.withExport);
                break;
            case EEngineType.LongitudinalStaticSection:
                if (htmlImgObj?.resultCalculation) {
                    htmlImgObj!.resultCalculation![preFsSeismic] = undefined;
                    htmlImgObj!.resultCalculation![postFsSeismic] = undefined;
                }
                engine = longitudinalSection(stage, htmlImgObj!, convertToPixel, props.onlySlopeAndLoad, props.withExport);
                break;
            case EEngineType.LongitudinalSeismicSection:
                if (htmlImgObj?.resultCalculation) {
                    htmlImgObj!.resultCalculation![preFsStatic] = undefined;
                    htmlImgObj!.resultCalculation![postFsStatic] = undefined;
                }
                engine = longitudinalSection(stage, htmlImgObj!, convertToPixel, props.onlySlopeAndLoad, props.withExport);
                break;

            case EEngineType.TransversalSectionPrimaryDrainageTrench:
                legendArr = [{ name: initialGroudwater, logo: initialWaterLegend }];
                if (!props.onlySlopeAndLoad) {
                    legendArr.push({ name: drainageLayingPlan, logo: drainageLayingPlanLegend }, { name: drain, logo: drainHorizontalLegend });
                    if (project.data?.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench].product?.hasPipe) {
                        legendArr.push({ name: drainPipe, logo: drainPipeLegend, width: 10 });
                    }
                }
                if (project.data?.load?.distributedOverload! > 0) {
                    legendArr.push({ name: distributedOverloadLegend, logo: overloadLegend, width: 10, height: 15 });
                }
                setLegendData(legendArr);
                engine = transversalSection(EDrainageTrenchType.PrimaryDrainageTrench, stage, htmlImgObj!, convertToPixel!, props.onlySlopeAndLoad, props.withExport);
                break;
            case EEngineType.TransversalSectionSecondaryDrainageTrench:
                legendArr = [{ name: initialGroudwater, logo: initialWaterLegend }];
                if (!props.onlySlopeAndLoad) {
                    legendArr.push({ name: drainageLayingPlan, logo: drainageLayingPlanLegend }, { name: drain, logo: drainHorizontalLegend });
                    if (project.data?.drainageTrenches![EDrainageTrenchType.SecondaryDrainageTrench].product?.hasPipe) {
                        legendArr.push({ name: drainPipe, logo: drainPipeLegend, width: 10 });
                    }
                }
                if (project.data?.load?.distributedOverload! > 0) {
                    legendArr.push({ name: distributedOverloadLegend, logo: overloadLegend, width: 10, height: 15 });
                }
                setLegendData(legendArr);
                engine = transversalSection(EDrainageTrenchType.SecondaryDrainageTrench, stage, htmlImgObj!, convertToPixel!, props.onlySlopeAndLoad, props.withExport);
                break;
            case EEngineType.ResultCalculation:
                break;
        }
        var scaleBy = Math.min((0.85 * stage.width()) / engine!.width, (0.85 * stage.height()) / engine!.height);
        stage.add(engine!.layer);
        stage.on('wheel', (e: any) => {
            e.evt.preventDefault();
            let direction = e.evt.deltaY > 0 ? 1 : -1;
            handleZoom(direction, 0.9);
        });
        handleZoom(1, scaleBy);
    };

    React.useEffect(() => {
        if (matches && sizeWindow.width && htmlImgObjContext && canvasRef.current?.clientHeight! > 0 && canvasRef.current?.clientWidth! > 0) {
            draw();
            if (result.status == EReduxState.fulfilled && props.withExport && htmlImgObjContext.resultCalculation) {
                switch (props.type) {
                    case EEngineType.PreliminaryCalculation:
                        break;
                    case EEngineType.GeneralView:
                        dispatch(setGeneralViewImage(stage.toDataURL({ pixelRatio: 5, height: 300 })));
                        break;
                    case EEngineType.LongitudinalStaticSection:
                        dispatch(setLongitudinalStaticSectionImage(stage.toDataURL({ pixelRatio: 5, height: 300 })));
                        break;
                    case EEngineType.LongitudinalSeismicSection:
                        dispatch(setLongitudinalSeismicSectionImage(stage.toDataURL({ pixelRatio: 5, height: 300 })));
                        break;
                    case EEngineType.TransversalSectionPrimaryDrainageTrench:
                        dispatch(setTransversalSectionPrimaryDrainageTrenchImage(stage.toDataURL({ pixelRatio: 5, height: 300 })));
                        break;
                    case EEngineType.TransversalSectionSecondaryDrainageTrench:
                        dispatch(setTransversalSectionSecondaryDrainageTrenchImage(stage.toDataURL({ pixelRatio: 5, height: 300 })));
                        break;
                }
            }
        }
    }, [reset, project, htmlImgObjContext, sizeWindow]);

    return (
        <Stack position='relative' height='100%'>
            <Stack variant='actionEngine' position={'absolute'} right={0} zIndex={1000}>
                <IconButton aria-label='zoomIn' onClick={() => handleZoom(-1, 0.9)}>
                    <ZoomInIcon color='white' />
                </IconButton>
                <IconButton aria-label='zoomOut' onClick={() => handleZoom(1, 0.9)}>
                    <ZoomOutIcon color='white' />
                </IconButton>
                <IconButton aria-label='zoomOutMap' onClick={() => setReset(prev => !prev)}>
                    <ZoomOutMapIcon color='white' />
                </IconButton>
            </Stack>
            {(props.type == EEngineType.LongitudinalSection ||
                props.type == EEngineType.TransversalSectionPrimaryDrainageTrench ||
                props.type == EEngineType.TransversalSectionSecondaryDrainageTrench) && (
                <Stack position={'absolute'} left={0} top={0} zIndex={1000}>
                    <Legend data={legendData} />
                </Stack>
            )}
            <Stack ref={canvasRef} style={{ position: 'absolute', height: '100%', width: '100%', backgroundImage: `url(${grid})` }}></Stack>
        </Stack>
    );
};
